import { formatCommunityReportCopy } from "../../community-report-copy.js";

export const HEX_TIME_WINDOW_DAYS = 30;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATE_LABEL_FORMATTERS = Object.freeze({
  EN: new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" }),
  ES: new Intl.DateTimeFormat("es-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" }),
});

export function discoverHexReportTimeline(reports, { windowDays = HEX_TIME_WINDOW_DAYS } = {}) {
  if (!Array.isArray(reports) || reports.length === 0) {
    throw new TypeError("Cannot discover a hex report timeline without reports.");
  }
  const dates = reports.map((report, index) => reportDateFor(report, index));
  const orderedDates = [...dates].sort();
  const availableStartDate = orderedDates[0];
  const endDate = orderedDates.at(-1);
  const startDate = availableStartDate > addCalendarDays(endDate, -windowDays)
    ? availableStartDate
    : addCalendarDays(endDate, -windowDays);
  return Object.freeze({
    startDate,
    endDate,
    availableStartDate,
    days: Object.freeze(calendarDaysBetween(startDate, endDate)),
  });
}

export function filterReportsByTime(reports, { startDate, selectedDate }) {
  assertDateOnly(startDate, "start date");
  assertDateOnly(selectedDate, "selected date");
  if (startDate > selectedDate) {
    throw new RangeError("Report start date must not be after the selected date.");
  }
  if (!Array.isArray(reports)) throw new TypeError("Expected an array of hex reports.");
  return reports.filter((report, index) => {
    const reportDate = reportDateFor(report, index);
    return reportDate >= startDate && reportDate <= selectedDate;
  });
}

// The human H3 renderer keeps its established name, while animal and
// environment overlays use the same date-only filter without a second copy.
export const filterHexReportsByTime = filterReportsByTime;

export function calendarDaysBetween(startDate, endDate) {
  const start = utcDateFromDateOnly(startDate, "timeline start date");
  const end = utcDateFromDateOnly(endDate, "timeline end date");
  if (start > end) throw new RangeError("Timeline start date must not be after the end date.");
  const days = [];
  for (const date = new Date(start); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    days.push(dateOnlyFromUtcDate(date));
  }
  return days;
}

export function addCalendarDays(dateOnly, days) {
  const date = utcDateFromDateOnly(dateOnly, "date");
  date.setUTCDate(date.getUTCDate() + Number(days));
  return dateOnlyFromUtcDate(date);
}

export function formatHexReportDate(dateOnly, language = "EN") {
  return (DATE_LABEL_FORMATTERS[language] || DATE_LABEL_FORMATTERS.EN).format(utcDateFromDateOnly(dateOnly, "date"));
}

export function timeSummaryForHexReports({ startDate, selectedDate }, language = "EN") {
  return formatCommunityReportCopy(language, "hex_time_from", {
    range: formatHexReportRange(startDate, selectedDate, language),
  });
}

export function formatHexReportRange(startDate, endDate, language = "EN") {
  const startLabel = formatHexReportDate(startDate, language);
  const endLabel = formatHexReportDate(endDate, language);
  const through = formatCommunityReportCopy(language, "hex_time_through");
  if (language === "ES") return `${startLabel} ${through} ${endLabel}`;
  return startDate.slice(0, 4) === endDate.slice(0, 4)
    ? `${startLabel.replace(/, \d{4}$/, "")} ${through} ${endLabel}`
    : `${startLabel} ${through} ${endLabel}`;
}

export function assertDateOnly(value, label = "date") {
  utcDateFromDateOnly(value, label);
}

function reportDateFor(report, index) {
  if (!report || typeof report !== "object") {
    throw new TypeError(`Invalid report at index ${index}: expected an object with reportDate.`);
  }
  try {
    assertDateOnly(report.reportDate, `reportDate for report at index ${index}${report.id ? ` (id ${report.id})` : ""}`);
  } catch (error) {
    throw new TypeError(error.message);
  }
  return report.reportDate;
}

function utcDateFromDateOnly(value, label) {
  if (typeof value !== "string" || !DATE_ONLY_PATTERN.test(value)) {
    throw new TypeError(`Invalid ${label}: expected YYYY-MM-DD.`);
  }
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new RangeError(`Invalid ${label}: ${value} is not a calendar date.`);
  }
  return date;
}

function dateOnlyFromUtcDate(date) {
  return date.toISOString().slice(0, 10);
}
